import { useCallback, useContext, useEffect, useState } from 'react';
import { SessionContext } from '../state/Session';
import UserRoles from '../types/UserRoles';

// Simple in-memory debug state with event-based sync
let debugModeState = false;
const DEBUG_MODE_EVENT = 'ospitek:debugMode';

// Global keyboard listener (registered once)
let keyboardListenerRegistered = false;

const registerGlobalKeyboardListener = () => {
  if (keyboardListenerRegistered) return;
  keyboardListenerRegistered = true;

  window.addEventListener('keydown', (e: KeyboardEvent) => {
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      (e.target as HTMLElement).isContentEditable
    ) {
      return;
    }

    if ((e.shiftKey || e.ctrlKey) && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      debugModeState = !debugModeState;
      window.dispatchEvent(new Event(DEBUG_MODE_EVENT));
    }
  });
};

export const useDebugMode = (externalIsSuperAdmin?: boolean) => {
  const sessionContext = useContext(SessionContext) as any;
  const roles = (sessionContext.session?.user?.metadata ?? []) as string[];
  const internalIsSuperAdmin = roles.indexOf(UserRoles.SuperAdmin) >= 0;
  const isSuperAdmin = externalIsSuperAdmin ?? internalIsSuperAdmin;

  const [debugModeEnabled, setDebugModeEnabled] = useState(debugModeState);

  // Register global keyboard listener once when a SuperAdmin uses this hook
  useEffect(() => {
    if (isSuperAdmin) {
      registerGlobalKeyboardListener();
    }
  }, [isSuperAdmin]);

  // Sync with global state changes
  useEffect(() => {
    const handler = () => setDebugModeEnabled(debugModeState);
    window.addEventListener(DEBUG_MODE_EVENT, handler);
    return () => window.removeEventListener(DEBUG_MODE_EVENT, handler);
  }, []);

  // Toggle debug mode (for programmatic use)
  const toggleDebugMode = useCallback(() => {
    debugModeState = !debugModeState;
    setDebugModeEnabled(debugModeState);
    window.dispatchEvent(new Event(DEBUG_MODE_EVENT));
  }, []);

  return {
    isSuperAdmin,
    debugModeEnabled,
    canBypassPermissions: isSuperAdmin && debugModeEnabled,
    toggleDebugMode,
  };
};

export default useDebugMode;
